import { Card } from '@material-ui/core';
import React from 'react';

import { track } from '~/src/dash/analytics/analytics';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { ButtonV2, Col, Cols, Panel, Panels, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  navigate,
  RouteAudienceManagerLiverampMarketplace,
  RouteSegmentsLiverampDistribution,
} from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { isLiverampIntegrationEnabled } from '~/src/dash/utils/publishers';

export const DataPartnerLanding = (): React.ReactNode => {
  const { session, user, isSuperUser } = useBaseStore();
  const disabled = !isLiverampIntegrationEnabled(session.publisher.LiverampDistributionManagerID);

  const MarketplaceSegmentButton = () => {
    return (
      <>
        <ButtonV2
          clearBackground
          onClick={() => {
            track('LiveRamp - Access Data Marketplace', { isSuperUser }, user.user);
            navigate(
              RouteAudienceManagerLiverampMarketplace,
              extractRouteParams({ session, user }),
            );
            return;
          }}
          style={{ padding: '5px 16px' }}
          disabled={disabled}
        >
          <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
            <Col marginTop='6px'>
              <IconV2 name='boxSearch' color={disabled ? publicaColors.gray : publicaColors.blue} />
            </Col>
            <Col>
              <Text small bold color={disabled ? publicaColors.gray : publicaColors.blue}>
                Data Marketplace Segments
              </Text>
            </Col>
          </Cols>
        </ButtonV2>
      </>
    );
  };

  const ManageDistributionButton = () => {
    return (
      <>
        <ButtonV2
          clearBackground
          onClick={() => {
            track('LiveRamp - Access Manage Distribution', { isSuperUser }, user.user);
            navigate(RouteSegmentsLiverampDistribution, extractRouteParams({ session, user }));
            return;
          }}
          style={{ padding: '5px 16px' }}
          disabled={disabled}
        >
          <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
            <Col paddingTop='6px'>
              <IconV2
                name='distribution'
                color={disabled ? publicaColors.gray : publicaColors.blue}
              />
            </Col>
            <Col>
              <Text small bold color={disabled ? publicaColors.gray : publicaColors.blue}>
                Manage Distribution
              </Text>
            </Col>
          </Cols>
        </ButtonV2>
      </>
    );
  };

  return (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
    >
      <Panel>
        <Rows>
          <Row paddingBottom={spacing.base} noVerticalPadding>
            <Text small bold>
              Browse and buy segments from our partners' data marketplace and manage your segment
              distribution in one place.
            </Text>
          </Row>
          <Row noVerticalPadding>
            <Card
              style={{
                width: '376px',
                height: '37px',
                backgroundColor: '#D5D8F947',
                boxShadow: 'none',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Text
                style={{
                  fontSize: '12px',
                  display: 'flex',
                  justifyContent: 'center',
                  fontWeight: 500,
                }}
              >
                <img
                  src='/assets/shared/svg/DatapartnerBadge.svg'
                  style={{ marginRight: spacing.small }}
                />
                Reach out to your account manager to enable data partners.
              </Text>
            </Card>
          </Row>
        </Rows>
        <Panel>
          <Rows>
            <Row paddingBottom={spacing.extraSmall} paddingTop={spacing.large}>
              <Text small bold>
                Our Data Partners
              </Text>
            </Row>
            <Row noVerticalPadding paddingBottom={spacing.base}>
              <Panel highlighted>
                <Cols
                  style={{
                    alignItems: 'center',
                  }}
                  gap={spacing.base}
                >
                  <Col>
                    <img
                      src='/assets/shared/png/LiveRamp-Logo.png'
                      style={{ width: '150px', marginLeft: '20px' }}
                    />
                  </Col>
                  <Col flex>
                    <Rows>
                      <Row>
                        <Text extraSmall bold>
                          LiveRamp helps you build, configure, maintain and easily connect customer
                          data from any data sources.{' '}
                          <span
                            onClick={() => {
                              track('LiveRamp - Learn more links', { isSuperUser }, user.user);
                              window.open(
                                'https://publica-project.atlassian.net/wiki/spaces/wiki/pages/3264086017/Using+LiveRamp+Segments',
                                '_blank',
                              );
                              return;
                            }}
                            style={{ color: publicaColors.blue, cursor: 'pointer' }}
                          >
                            Learn More
                          </span>
                        </Text>
                      </Row>

                      <Row noVerticalPadding paddingBottom={spacing.base}>
                        <Cols>
                          <Col>
                            <Panel highlighted basePadding>
                              <Text small style={{ fontWeight: 700 }}>
                                MARKETPLACE
                              </Text>
                              <Text extraSmall bold style={{ color: '#434343' }}>
                                Browse and request data marketplace segments to add to your
                                distribution
                              </Text>
                              <MarketplaceSegmentButton />
                            </Panel>
                          </Col>

                          <Col marginLeft={spacing.extraLarge}>
                            <Panel highlighted basePadding>
                              <Text small style={{ fontWeight: 700 }}>
                                SEGMENT DISTRIBUTION
                              </Text>
                              <Text extraSmall bold style={{ color: '#434343' }}>
                                Manage existing and requested segments in your distribution
                              </Text>
                              <ManageDistributionButton />
                            </Panel>
                          </Col>
                        </Cols>
                      </Row>
                    </Rows>
                  </Col>
                </Cols>
              </Panel>
            </Row>
            <Row noVerticalPadding>
              <Panel highlighted hugeVerticalPadding>
                <Cols
                  style={{
                    alignItems: 'center',
                  }}
                  gap={spacing.base}
                >
                  <Col>
                    <img
                      src='/assets/shared/png/TransunionLogo.png'
                      style={{ width: '150px', marginLeft: '40px' }}
                    />
                  </Col>
                  <Col flex>
                    <Text extraSmall bold style={{ marginLeft: '30px' }}>
                      TransUnion Audience Solution help users create audiences and target customers.
                      <span
                        onClick={() => {
                          track('LiveRamp - Learn more links', { isSuperUser }, user.user);
                          window.open(
                            'https://publica-project.atlassian.net/wiki/spaces/wiki/pages/2189328448/Third-Party+Audience+Segments',
                            '_blank',
                          );
                          return;
                        }}
                        style={{ color: publicaColors.blue, cursor: 'pointer' }}
                      >
                        Learn More
                      </span>
                    </Text>
                  </Col>
                </Cols>
              </Panel>
            </Row>
            <Row>
              <Panel highlighted hugeVerticalPadding>
                <Cols
                  style={{
                    alignItems: 'center',
                  }}
                  gap={spacing.base}
                >
                  <Col>
                    <img
                      src='/assets/shared/png/CadentLogo.png'
                      style={{ width: '150px', marginLeft: '40px' }}
                    />
                  </Col>
                  <Col flex>
                    <Text extraSmall bold style={{ marginLeft: '30px' }}>
                      Cadent audience reach helps you navigate and extend your reach across the TV
                      ecosystem.
                      <span
                        onClick={() => {
                          track('LiveRamp - Learn more links', { isSuperUser }, user.user);
                          window.open(
                            'https://publica-project.atlassian.net/wiki/spaces/wiki/pages/2189328448/Third-Party+Audience+Segments',
                            '_blank',
                          );
                          return;
                        }}
                        style={{ color: '#4952FF', cursor: 'pointer' }}
                      >
                        Learn More
                      </span>
                    </Text>
                  </Col>
                </Cols>
              </Panel>
            </Row>
          </Rows>
        </Panel>
      </Panel>
    </Panels>
  );
};
